import React from 'react';
import { Box, Container, Typography, styled, Stack, Chip, Paper, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TerminalIcon from '@mui/icons-material/Terminal';

const WebSection = styled(Box)({
  padding: '100px 0',
  background: '#030712',
  color: '#f8fafc',
  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  position: 'relative',
  overflow: 'hidden'
});

const GlassCard = styled(Paper)({
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(16px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
  overflow: 'hidden',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
});

const TechPill = styled(Chip)({
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  color: '#cbd5e1',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  fontSize: '0.75rem',
  fontWeight: 600,
  height: '28px',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    borderColor: 'rgba(139, 92, 246, 0.4)',
    color: '#a78bfa'
  }
});

const FeaturePoint = ({ title, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <CheckCircleIcon sx={{ color: '#8b5cf6', fontSize: '1.1rem', mt: 0.3 }} />
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.825rem', lineHeight: 1.5, mt: 0.3 }}>
        {text}
      </Typography>
    </Box>
  </Stack>
);

const WebDetail = () => {
  const techStack = [
    "Next.js 14+", "React 19", "TypeScript", "Tailwind CSS", 
    "MUI v6", "SSR / SSG", "Node.js", "PostgreSQL", "GraphQL"
  ];

  return (
    <WebSection id="web-detail">
      {/* Background Radial Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '20%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, rgba(3, 7, 18, 0) 70%)',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          
          {/* Left Column: Glassmorphic IDE/Browser Mockup */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <Box sx={{ position: 'relative' }}>
                
                {/* Main Code/Preview Shell */}
                <GlassCard sx={{ position: 'relative' }}>
                  {/* Window Bar */}
                  <Box 
                    sx={{ 
                      px: 2.5, 
                      py: 1.8, 
                      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      bgcolor: 'rgba(15, 23, 42, 0.8)'
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                    </Stack>
                    <Box 
                      sx={{ 
                        px: 2, 
                        py: 0.4, 
                        borderRadius: '6px', 
                        bgcolor: 'rgba(255, 255, 255, 0.04)', 
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        color: '#64748b', 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <TerminalIcon sx={{ fontSize: 14, color: '#8b5cf6' }} /> nextjs-pwa-app.vercel.app
                    </Box>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                  </Box>

                  {/* Image Graphic */}
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src="https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&q=80&w=1000"
                      alt="Modern Web Architecture"
                      sx={{
                        width: '100%',
                        height: { xs: '280px', sm: '360px' },
                        objectFit: 'cover',
                        display: 'block',
                        filter: 'brightness(0.85) contrast(1.1)'
                      }}
                    />
                    <Box 
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0) 40%, rgba(15, 23, 42, 0.95) 100%)'
                      }}
                    />
                  </Box>

                  {/* Code Snippet Footer Overlay */}
                  <Box sx={{ p: 3, pt: 1, bgcolor: 'rgba(15, 23, 42, 0.95)' }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#8b5cf6', display: 'block', mb: 0.5 }}>
                      // Core Web Vitals Optimized
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: '0.8rem' }}>
                      export default async function Page() &#123;<br />
                      &nbsp;&nbsp;const data = await fetchApi(&#123; revalidate: 3600 &#125;);<br />
                      &nbsp;&nbsp;return &lt;ServerComponent data=&#123;data&#125; /&gt;;<br />
                      &#125;
                    </Typography>
                  </Box>
                </GlassCard>

                {/* Floating Metric Badge 1: Performance */}
                <motion.div
                  initial={{ opacity: 0, x: -20, y: 10 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  style={{ position: 'absolute', top: '-20px', left: '-20px', zIndex: 3 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.8,
                      borderRadius: '16px',
                      background: 'rgba(15, 23, 42, 0.85)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', width: 40, height: 40 }}>
                      <SpeedIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem', display: 'block' }}>
                        Lighthouse Score
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#10b981', fontWeight: 800, fontSize: '0.95rem' }}>
                        100/100 Performance
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>

                {/* Floating Metric Badge 2: SSR Tag */}
                <motion.div
                  initial={{ opacity: 0, x: 20, y: -10 }}
                  whileInView={{ opacity: 1, x: 0, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  style={{ position: 'absolute', bottom: '30px', right: '-20px', zIndex: 3 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      py: 1.2,
                      px: 2,
                      borderRadius: '14px',
                      background: 'rgba(15, 23, 42, 0.9)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                    <Typography variant="caption" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.8rem' }}>
                      Server Components Active
                    </Typography>
                  </Paper>
                </motion.div>

              </Box>
            </motion.div>
          </Grid>

          {/* Right Column: Content & Engineering Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Category Pill */}
              <Chip
                icon={<LanguageIcon sx={{ fontSize: '16px !important', color: '#a78bfa' }} />}
                label="MODERN WEB ECOSYSTEM"
                sx={{
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                  color: '#a78bfa',
                  border: '1px solid rgba(139, 92, 246, 0.25)',
                  fontWeight: 700,
                  fontSize: '0.725rem',
                  letterSpacing: 1.2,
                  mb: 2.5,
                  py: 1.8,
                  px: 0.5
                }}
              />
              
              {/* Main Headline */}
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2, 
                  fontSize: { xs: '2rem', md: '2.6rem' }, 
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: '#f8fafc'
                }}
              >
                Fast, Scalable & <br />
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 50%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  SEO-First Web Systems
                </Box>
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#94a3b8', mb: 3.5, fontSize: '1rem', lineHeight: 1.7 }}>
                Engineered for maximum execution speed, zero layout shift, and instant server rendering. I craft high-converting web applications leveraging <b>Next.js App Router</b>, <b>React 19</b>, and modern cloud deployment architectures.
              </Typography>

              {/* Feature Highlights Grid */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor: 'rgba(15, 23, 42, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '16px'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#f8fafc', mb: 1.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LanguageIcon sx={{ fontSize: '1.1rem', color: '#8b5cf6' }} /> Frontend Mastery
                    </Typography>
                    <Stack spacing={1.5}>
                      <FeaturePoint title="React Server Components" text="Zero client-side JavaScript overhead for static layouts." />
                      <FeaturePoint title="Dynamic Edge Routing" text="Sub-millisecond routing and localized state management." />
                      <FeaturePoint title="Tailwind & Material UI" text="Scalable design systems with accessible UI tokens." />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor: 'rgba(15, 23, 42, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '16px'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#f8fafc', mb: 1.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RocketLaunchIcon sx={{ fontSize: '1.1rem', color: '#3b82f6' }} /> Performance & SEO
                    </Typography>
                    <Stack spacing={1.5}>
                      <FeaturePoint title="SSR & SSG Architectures" text="Pre-rendered pages for optimal search indexing." />
                      <FeaturePoint title="Core Web Vitals First" text="Targeting 90+ mobile scores across LCP, FID, and CLS." />
                      <FeaturePoint title="API Route Handlers" text="Secure serverless functions and REST/GraphQL endpoints." />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* Tech Stack Chip Cloud */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                  Core Tech Stack
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {techStack.map((tech, idx) => (
                    <TechPill key={idx} label={tech} />
                  ))}
                </Box>
              </Box>

              {/* Full-Stack Infrastructure Card */}
              <Paper
                elevation={0}
                sx={{ 
                  p: 2.5, 
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%)', 
                  borderRadius: '16px', 
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2
                }}
              >
                <Avatar sx={{ bgcolor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                  <StorageIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                    Full-Stack & Database Synchronization
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.3, lineHeight: 1.4 }}>
                    Seamless connections between client frontends and Node.js backends, PostgreSQL, MongoDB, or Firebase.
                  </Typography>
                </Box>
              </Paper>

            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </WebSection>
  );
};

export default WebDetail;